/**
 * BagScanOrPick — Sender step (Item: scan QR or pick a bag from list).
 *
 * UX:
 *  1. "Iniciar leitura QR" → opens device camera via html5-qrcode
 *  2. Or "Não consegue ler? Escolher manualmente" → shows list of available bags
 *     filtered to the pickup point
 *  3. After scan/pick → simulated pairing animation → POST /pair-bag
 *  4. Then "Coloque o objeto e feche a bolsa" → POST /seal → onSealed()
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Scan, QrCode, Package, CheckCircle, Bluetooth, Lock, ListBullets, X } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BagScanOrPick({ shipment, onSealed }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("idle"); // idle | scanning | picking | pairing | paired | sealing | sealed
  const [error, setError] = useState("");
  const [pairedBag, setPairedBag] = useState(null);
  const [availableBags, setAvailableBags] = useState([]);
  const scannerRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup the scanner on unmount
      if (cleanupRef.current) {
        try { cleanupRef.current(); } catch (e) { /* ignore */ }
      }
    };
  }, []);

  const startScanner = async () => {
    setError("");
    setPhase("scanning");
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const target = scannerRef.current;
      if (!target) return;
      const scanner = new Html5Qrcode(target.id);
      const onScanSuccess = async (decodedText) => {
        try { await scanner.stop(); } catch (e) { /* ignore */ }
        try { scanner.clear(); } catch (e) { /* ignore */ }
        cleanupRef.current = null;
        await pairWithCode(decodedText);
      };
      const onScanError = () => { /* ignore frame-level errors */ };
      cleanupRef.current = async () => {
        try { await scanner.stop(); } catch (e) { /* ignore */ }
        try { scanner.clear(); } catch (e) { /* ignore */ }
      };
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        onScanSuccess,
        onScanError
      );
    } catch (e) {
      setError(t("scan.no_camera"));
      setPhase("idle");
    }
  };

  const stopScanner = async () => {
    if (cleanupRef.current) {
      try { await cleanupRef.current(); } catch (e) { /* ignore */ }
      cleanupRef.current = null;
    }
    setPhase("idle");
  };

  const openPicker = async () => {
    setError("");
    setPhase("picking");
    try {
      const res = await axios.get(`${API}/bags/available`, {
        params: shipment?.pickup_point ? { pickup_point: shipment.pickup_point } : {},
      });
      setAvailableBags(res.data || []);
    } catch (e) {
      setError(t("scan.bags_fetch_error"));
    }
  };

  const pairWithCode = async (scannedCode) => {
    setPhase("pairing");
    setError("");
    try {
      const res = await axios.post(`${API}/shipments/${shipment.tracking_code}/pair-bag`,
        { scanned_code: scannedCode });
      setPairedBag({ bag_code: res.data.bag_code, bag_type: res.data.bag_type });
      // Simulate pairing animation duration
      setTimeout(() => setPhase("paired"), 1200);
    } catch (e) {
      setError(e?.response?.data?.detail || t("scan.pair_error"));
      setPhase("idle");
    }
  };

  const sealBag = async () => {
    setPhase("sealing");
    try {
      const res = await axios.post(`${API}/shipments/${shipment.tracking_code}/seal`);
      setPhase("sealed");
      setTimeout(() => onSealed && onSealed(res.data), 800);
    } catch (e) {
      setError(t("scan.seal_error"));
      setPhase("paired");
    }
  };

  if (!shipment) {
    return (
      <div className="text-center p-8" data-testid="scan-pick-loading">
        <Package size={32} style={{ color: "var(--text-muted)", margin: "0 auto" }} />
        <p style={{ color: "var(--text-muted)", marginTop: 8 }}>{t("scan.loading")}</p>
      </div>
    );
  }

  return (
    <div data-testid="bag-scan-or-pick" className="animate-fade-in text-center">
      <div style={{ width: 80, height: 80, borderRadius: 20, background: "rgba(37,99,235,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Scan size={40} weight="duotone" style={{ color: "#2563eb" }} />
      </div>
      <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", marginBottom: 4 }}>{t("scan.title")}</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: 24 }}>{t("scan.subtitle")}</p>

      {phase === "idle" && (
        <div className="flex flex-col gap-3" style={{ maxWidth: 360, margin: "0 auto" }}>
          <button
            data-testid="scan-start-camera-btn"
            onClick={startScanner}
            className="flex items-center justify-center gap-2"
            style={{ padding: "14px 24px", borderRadius: 12, border: "none", background: "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, cursor: "pointer", fontSize: "0.9rem" }}
          >
            <QrCode size={18} weight="bold" /> {t("scan.start_camera")}
          </button>
          <button
            data-testid="scan-open-picker-btn"
            onClick={openPicker}
            className="flex items-center justify-center gap-2"
            style={{ padding: "12px 20px", borderRadius: 12, border: "1px solid var(--border-color)", background: "var(--surface)", color: "var(--text-main)", fontFamily: "Outfit", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem" }}
          >
            <ListBullets size={16} /> {t("scan.pick_manually")}
          </button>
        </div>
      )}

      {phase === "scanning" && (
        <div style={{ maxWidth: 360, margin: "0 auto" }}>
          <div id="qr-scan-region" ref={scannerRef} data-testid="qr-scan-region" style={{ borderRadius: 14, overflow: "hidden", background: "#000" }} />
          <button
            data-testid="scan-stop-btn"
            onClick={stopScanner}
            className="flex items-center justify-center gap-2 mt-3"
            style={{ padding: "10px 18px", borderRadius: 9999, border: "1px solid var(--border-color)", background: "transparent", cursor: "pointer", fontSize: "0.8rem", color: "var(--text-muted)", margin: "12px auto 0" }}
          >
            <X size={14} /> {t("scan.cancel")}
          </button>
        </div>
      )}

      {phase === "picking" && (
        <div style={{ maxWidth: 420, margin: "0 auto" }}>
          {availableBags.length === 0 ? (
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>{t("scan.no_bags_available")}</p>
          ) : (
            <div className="flex flex-col gap-2" data-testid="bag-picker-list">
              {availableBags.map((b) => (
                <button
                  key={b.bag_code}
                  data-testid={`bag-pick-${b.bag_code}`}
                  onClick={() => pairWithCode(b.bag_code)}
                  className="flex items-center justify-between"
                  style={{ padding: "12px 16px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--surface)", cursor: "pointer", textAlign: "left" }}
                >
                  <div className="flex items-center gap-3">
                    <Bluetooth size={16} weight="duotone" style={{ color: "var(--primary)" }} />
                    <div>
                      <div className="bag-code" style={{ fontSize: "0.95rem" }}>{b.bag_code}</div>
                      <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{b.current_location || t("scan.unknown_location")}</div>
                    </div>
                  </div>
                  <span style={{ fontSize: "0.7rem", padding: "2px 8px", borderRadius: 9999, background: "rgba(45,74,62,0.06)", color: "var(--primary)", fontWeight: 600, textTransform: "uppercase" }}>{b.bag_type}</span>
                </button>
              ))}
            </div>
          )}
          <button
            data-testid="scan-back-from-picker-btn"
            onClick={() => setPhase("idle")}
            className="flex items-center justify-center gap-2 mt-3"
            style={{ padding: "10px 18px", borderRadius: 9999, border: "1px solid var(--border-color)", background: "transparent", cursor: "pointer", fontSize: "0.8rem", color: "var(--text-muted)", margin: "12px auto 0" }}
          >
            <X size={14} /> {t("scan.cancel")}
          </button>
        </div>
      )}

      {phase === "pairing" && (
        <div data-testid="bag-pairing" style={{ maxWidth: 360, margin: "0 auto" }}>
          <div className="bento-item p-6">
            <div className="flex items-center justify-center" style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(37,99,235,0.1)", margin: "0 auto 12px", animation: "pulse-blue 1.2s ease-in-out infinite" }}>
              <Bluetooth size={36} weight="duotone" style={{ color: "#2563eb" }} />
            </div>
            <p style={{ fontFamily: "Outfit", fontWeight: 600 }}>{t("scan.pairing")}</p>
            <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 4 }}>{t("scan.pairing_hint")}</p>
          </div>
          <style>{`@keyframes pulse-blue {0%,100% {transform: scale(1); opacity: 1;} 50% {transform: scale(1.08); opacity: 0.7;}}`}</style>
        </div>
      )}

      {(phase === "paired" || phase === "sealing") && pairedBag && (
        <div data-testid="bag-paired" style={{ maxWidth: 380, margin: "0 auto" }}>
          <div className="bento-item p-6">
            <div className="flex items-center justify-center gap-3" style={{ marginBottom: 12 }}>
              <CheckCircle size={22} weight="fill" style={{ color: "var(--success)" }} />
              <span className="bag-code" style={{ fontSize: "1.2rem" }}>{pairedBag.bag_code}</span>
            </div>
            <p style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1rem", marginBottom: 4 }}>{t("scan.paired_title")}</p>
            <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: 16 }}>{t("scan.paired_subtitle")}</p>
            <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(232,163,89,0.08)", fontSize: "0.85rem", color: "var(--warning)", display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <Package size={16} /> {t("scan.place_object")}
            </div>
            <button
              data-testid="scan-seal-btn"
              onClick={sealBag}
              disabled={phase === "sealing"}
              className="flex items-center justify-center gap-2"
              style={{ width: "100%", padding: "14px 24px", borderRadius: 12, border: "none", background: phase === "sealing" ? "var(--text-muted)" : "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, cursor: phase === "sealing" ? "default" : "pointer", fontSize: "0.9rem", opacity: phase === "sealing" ? 0.7 : 1 }}
            >
              <Lock size={16} weight="bold" />
              {phase === "sealing" ? t("scan.sealing") : t("scan.seal_btn")}
            </button>
          </div>
        </div>
      )}

      {phase === "sealed" && (
        <div data-testid="bag-sealed" style={{ maxWidth: 360, margin: "0 auto" }}>
          <div className="bento-item p-6 text-center">
            <CheckCircle size={48} weight="fill" style={{ color: "var(--success)", margin: "0 auto 8px" }} />
            <p style={{ fontFamily: "Outfit", fontWeight: 700 }}>{t("scan.sealed_title")}</p>
            <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: 4 }}>{t("scan.sealed_hint")}</p>
          </div>
        </div>
      )}

      {error && <p data-testid="scan-error" style={{ marginTop: 12, fontSize: "0.8rem", color: "var(--error)" }}>{error}</p>}
    </div>
  );
}
